import clsx from 'clsx';
import type { ReactElement, ReactNode } from 'react';

import type { AiRunStatus } from '@mtgcraft/shared';

export interface AiChipProps {
  children: ReactNode;
  tone?: 'neutral' | 'accent' | 'success' | 'warning' | 'danger';
  title?: string;
  className?: string;
}

const toneStyles: Record<NonNullable<AiChipProps['tone']>, string> = {
  neutral: 'bg-storm/60 text-silver',
  accent: 'bg-aether/20 text-aether',
  success: 'bg-success/20 text-success',
  warning: 'bg-gold/20 text-gold',
  danger: 'bg-danger/20 text-danger',
};

export function AiChip({ children, tone = 'neutral', title, className }: AiChipProps): ReactElement {
  return (
    <span
      title={title}
      className={clsx('self-start rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap', toneStyles[tone], className)}
    >
      {children}
    </span>
  );
}

const STATUS_TONE: Record<AiRunStatus, NonNullable<AiChipProps['tone']>> = {
  running: 'accent',
  succeeded: 'success',
  invalid_output: 'warning',
  failed: 'danger',
};

const STATUS_LABEL: Record<AiRunStatus, string> = {
  running: 'Running',
  succeeded: 'Success',
  invalid_output: 'Invalid output',
  failed: 'Failed',
};

export function AiStatusChip({ status }: { status: AiRunStatus }): ReactElement {
  return <AiChip tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</AiChip>;
}

export interface AiModelChipsProps {
  model: string;
  reasoningEffort: string;
  verbosity: string;
  className?: string;
}

export function AiModelChips({ model, reasoningEffort, verbosity, className }: AiModelChipsProps): ReactElement {
  return (
    <div className={clsx('flex flex-wrap items-center gap-1.5', className)}>
      <AiChip tone="accent" className="font-mono">
        {model}
      </AiChip>
      <AiChip title="Reasoning effort" className="font-mono">
        e:{reasoningEffort}
      </AiChip>
      <AiChip title="Output verbosity" className="font-mono">
        v:{verbosity}
      </AiChip>
    </div>
  );
}
